import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// apicore
import { APICore, setAuthorization } from "../../helpers/api/apiCore";

// helpers
import {
  login as loginApi,
  logout as logoutApi,
  forgotPassword as forgotPasswordApi,
} from "../../helpers/";

// actions
import { authApiResponseSuccess, authApiResponseError } from "./actions";

// constants
import { AuthActionTypes } from "./constants";

// analytics — login/logout are emitted from the auth flow itself, never inferred from
// route changes: both logout paths end in a full document load, so any component-level
// observer is destroyed before it could notice (ANALYTICS.md B3).
import { setUser, clearUser, track } from "../../analytics";
import { EVENTS } from "../../analytics/events";

interface UserData {
  payload: {
    username: string;
    password: string;
    fullname: string;
    email: string;
  };
  type: string;
}

const api = new APICore();

/**
 * Login the user
 * @param {*} payload - username and password
 */
function* login({
  payload: { email, password },
}: UserData): SagaIterator {
  try {
    const response = yield call(loginApi, { email, password });
    const user = response.data?.data;
    // NOTE - You can change this according to response format from your api
    api.setLoggedInUser(user);
    setAuthorization(user["access_token"]);
    setUser(user);
    track(EVENTS.LOGIN, { method: "password" });
    yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, user));
  } catch (error: any) {
    // A rate-limit refusal arrives as an AuthLimitError; the store keeps strings only.
    yield put(authApiResponseError(AuthActionTypes.LOGIN_USER, error instanceof Error ? error.message : error));
    api.setLoggedInUser(null);
    setAuthorization(null);
  }
}

/**
 * Logout the user
 */
function* logout(): SagaIterator {
  // M12: clear local state BEFORE the network call. Previously a tab refresh
  // (or browser kill) during the in-flight POST left the session token in
  // storage even though the user clicked "logout" — re-opening the tab kept
  // them authenticated against the still-valid JWT. Clearing first means even
  // a network failure leaves the user logged out on this device.
  try {
    // Emit before clearing: the event needs the user dimensions that clearUser() drops.
    track(EVENTS.LOGOUT, { reason: "user" });
    clearUser();
    api.setLoggedInUser(null);
    setAuthorization(null);
    try {
      yield call(logoutApi);
    } catch (apiErr) {
      // Backend revocation failed (network / 401) — local state is already
      // gone, the user is signed out client-side. Just swallow; the JWT will
      // expire on its TTL even without server-side blacklist.
    }
    yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
  } catch (error: any) {
    yield put(authApiResponseError(AuthActionTypes.LOGOUT_USER, error));
  }
}

function* forgotPassword({ payload: { email } }: UserData): SagaIterator {
  try {
    const response = yield call(forgotPasswordApi, { email });
    yield put(
      authApiResponseSuccess(AuthActionTypes.FORGOT_PASSWORD, response.data)
    );
  } catch (error: any) {
    yield put(authApiResponseError(AuthActionTypes.FORGOT_PASSWORD, error));
  }
}
export function* watchLoginUser() {
  yield takeEvery(AuthActionTypes.LOGIN_USER, login);
}

export function* watchLogout() {
  yield takeEvery(AuthActionTypes.LOGOUT_USER, logout);
}


export function* watchForgotPassword(): any {
  yield takeEvery(AuthActionTypes.FORGOT_PASSWORD, forgotPassword);
}

function* authSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogout),
    fork(watchForgotPassword),
  ]);
}

export default authSaga;
